import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, Trash2, Link, History } from "lucide-react";
import api from "../api";

const fetchProducts = () => api.get("/products").then(r => r.data);
const fetchAllSkus = () => api.get("/profit/by-sku").then(r => r.data);
const fetchCogsHistory = (pid) => api.get(`/products/${pid}/cogs-history`).then(r => r.data);

function CogsHistory({ product }) {
  const qc = useQueryClient();
  const [show, setShow] = useState(false);
  const [newEntry, setNewEntry] = useState({ cogs: "", effective_from: "", note: "" });

  const { data: history } = useQuery({
    queryKey: ["cogs-history", product.id],
    queryFn: () => fetchCogsHistory(product.id),
    enabled: show
  });

  const addEntry = useMutation({
    mutationFn: (data) => api.post(`/products/${product.id}/cogs-history`, data).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries(["cogs-history", product.id]);
      setNewEntry({ cogs: "", effective_from: "", note: "" });
    }
  });

  const deleteEntry = useMutation({
    mutationFn: (id) => api.delete(`/products/cogs-history/${id}`).then(r => r.data),
    onSuccess: () => qc.invalidateQueries(["cogs-history", product.id])
  });

  return (
    <div style={{ marginTop: "10px" }}>
      <button className="btn btn-outline btn-sm" onClick={() => setShow(!show)}>
        <History size={13} /> COGS History
      </button>

      {show && (
        <div style={{ marginTop: "10px", background: "#f8f9ff", padding: "12px", borderRadius: "8px" }}>
          <div style={{ fontWeight: 600, fontSize: "0.85rem", marginBottom: "10px" }}>
            COGS History — system picks correct cost per order date automatically
          </div>

          {/* Add new entry */}
          <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", alignItems: "flex-end", marginBottom: "12px" }}>
            <div>
              <label style={{ fontSize: "0.75rem", color: "#888", display: "block", marginBottom: "3px" }}>COGS per unit (₹)</label>
              <input type="number" value={newEntry.cogs}
                onChange={e => setNewEntry(p => ({ ...p, cogs: e.target.value }))}
                style={{ padding: "6px 10px", border: "1px solid #e0e0e0", borderRadius: "6px", width: "100px", fontSize: "0.875rem" }} />
            </div>
            <div>
              <label style={{ fontSize: "0.75rem", color: "#888", display: "block", marginBottom: "3px" }}>Effective from</label>
              <input type="date" value={newEntry.effective_from}
                onChange={e => setNewEntry(p => ({ ...p, effective_from: e.target.value }))}
                style={{ padding: "6px 10px", border: "1px solid #e0e0e0", borderRadius: "6px", fontSize: "0.875rem" }} />
            </div>
            <div>
              <label style={{ fontSize: "0.75rem", color: "#888", display: "block", marginBottom: "3px" }}>Note (optional)</label>
              <input value={newEntry.note} placeholder="e.g. New print run"
                onChange={e => setNewEntry(p => ({ ...p, note: e.target.value }))}
                style={{ padding: "6px 10px", border: "1px solid #e0e0e0", borderRadius: "6px", width: "160px", fontSize: "0.875rem" }} />
            </div>
            <button className="btn btn-primary btn-sm"
              onClick={() => addEntry.mutate({ cogs: parseFloat(newEntry.cogs), effective_from: newEntry.effective_from, note: newEntry.note })}>
              <Plus size={13} /> Add
            </button>
          </div>

          {/* History table */}
          {!history?.length ? (
            <div style={{ fontSize: "0.85rem", color: "#aaa" }}>
              No COGS history yet. Add an entry above — use today's COGS with your earliest date to cover all history.
            </div>
          ) : (
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.85rem" }}>
              <thead>
                <tr>
                  <th style={{ textAlign: "left", padding: "6px 10px", color: "#888", fontSize: "0.75rem" }}>Effective From</th>
                  <th style={{ textAlign: "left", padding: "6px 10px", color: "#888", fontSize: "0.75rem" }}>COGS/unit</th>
                  <th style={{ textAlign: "left", padding: "6px 10px", color: "#888", fontSize: "0.75rem" }}>Note</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {history.map(h => (
                  <tr key={h.id}>
                    <td style={{ padding: "6px 10px" }}>{h.effective_from}</td>
                    <td style={{ padding: "6px 10px", fontWeight: 600 }}>₹{h.cogs}</td>
                    <td style={{ padding: "6px 10px", color: "#888" }}>{h.note || "—"}</td>
                    <td style={{ padding: "6px 10px" }}>
                      <button onClick={() => deleteEntry.mutate(h.id)}
                        style={{ background: "none", border: "none", cursor: "pointer", color: "#e53e3e" }}>
                        <Trash2 size={13} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}

export default function Products() {
  const qc = useQueryClient();
  const { data: products } = useQuery({ queryKey: ["products"], queryFn: fetchProducts });
  const { data: allSkus } = useQuery({ queryKey: ["all-skus"], queryFn: fetchAllSkus });

  const [newProduct, setNewProduct] = useState({ product_name: "", cogs: "" });
  const [showAdd, setShowAdd] = useState(false);
  const [showMap, setShowMap] = useState(null);
  const [mapping, setMapping] = useState({ sku: "", fulfillment_type: "FBA" });

  const mappedSkus = new Set(products?.flatMap(p => p.skus.map(s => s.sku)) || []);
  const unmappedSkus = allSkus?.filter(s => !mappedSkus.has(s.sku)) || [];

  const createProduct = useMutation({
    mutationFn: (data) => api.post("/products", data).then(r => r.data),
    onSuccess: () => { qc.invalidateQueries(["products"]); setShowAdd(false); setNewProduct({ product_name: "", cogs: "" }); }
  });

  const mapSku = useMutation({
    mutationFn: ({ productId, sku, fulfillment_type }) =>
      api.post(`/products/${productId}/map-sku`, { sku, fulfillment_type }).then(r => r.data),
    onSuccess: () => { qc.invalidateQueries(["products"]); qc.invalidateQueries(["all-skus"]); setShowMap(null); setMapping({ sku: "", fulfillment_type: "FBA" }); }
  });

  const unmapSku = useMutation({
    mutationFn: (sku) => api.delete(`/products/unmap-sku/${sku}`).then(r => r.data),
    onSuccess: () => { qc.invalidateQueries(["products"]); qc.invalidateQueries(["all-skus"]); }
  });

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <div className="page-title">Map Products</div>
          <div className="page-subtitle">Group SKUs under products and manage COGS history</div>
        </div>
        <button className="btn btn-primary btn-sm" onClick={() => setShowAdd(!showAdd)}>
          <Plus size={14} /> New Product
        </button>
      </div>

      {unmappedSkus.length > 0 && (
        <div className="alert alert-warning">
          <strong>{unmappedSkus.length} SKUs not yet mapped:</strong>{" "}
          {unmappedSkus.map(s => s.sku).join(", ")}
        </div>
      )}

      {showAdd && (
        <div className="chart-card" style={{ marginBottom: "1rem" }}>
          <div className="chart-title">Add New Product</div>
          <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
            <div style={{ flex: 2 }}>
              <label style={{ fontSize: "0.75rem", color: "#888", display: "block", marginBottom: "4px" }}>Product Name</label>
              <input value={newProduct.product_name}
                onChange={e => setNewProduct(p => ({ ...p, product_name: e.target.value }))}
                placeholder="e.g. NEET PG NBE Solved Question Papers"
                style={{ width: "100%", padding: "8px 12px", border: "1px solid #e0e0e0", borderRadius: "8px", fontSize: "0.875rem" }} />
            </div>
            <div style={{ flex: 1 }}>
              <label style={{ fontSize: "0.75rem", color: "#888", display: "block", marginBottom: "4px" }}>Current COGS/unit (₹)</label>
              <input type="number" value={newProduct.cogs}
                onChange={e => setNewProduct(p => ({ ...p, cogs: e.target.value }))}
                placeholder="0"
                style={{ width: "100%", padding: "8px 12px", border: "1px solid #e0e0e0", borderRadius: "8px", fontSize: "0.875rem" }} />
            </div>
          </div>
          <div style={{ marginTop: "12px", display: "flex", gap: "8px" }}>
            <button className="btn btn-primary btn-sm"
              onClick={() => createProduct.mutate({ product_name: newProduct.product_name, cogs: parseFloat(newProduct.cogs || 0) })}>
              <Plus size={14} /> Create
            </button>
            <button className="btn btn-outline btn-sm" onClick={() => setShowAdd(false)}>Cancel</button>
          </div>
        </div>
      )}

      {!products?.length ? (
        <div className="empty">No products yet. Click "New Product" to get started.</div>
      ) : products.map(product => (
        <div key={product.id} className="chart-card" style={{ marginBottom: "1rem" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
            <div>
              <div style={{ fontWeight: 700, fontSize: "1rem" }}>📦 {product.product_name}</div>
              <div style={{ fontSize: "0.8rem", color: "#888", marginTop: "2px" }}>
                Default COGS: ₹{product.cogs}/unit · {product.skus.length} SKU{product.skus.length !== 1 ? "s" : ""}
              </div>
            </div>
            <button className="btn btn-outline btn-sm"
              onClick={() => setShowMap(showMap === product.id ? null : product.id)}>
              <Link size={14} /> Map SKU
            </button>
          </div>

          {/* COGS History */}
          <CogsHistory product={product} />

          {/* Map SKU form */}
          {showMap === product.id && (
            <div style={{ background: "#f0f4ff", padding: "12px", borderRadius: "8px", marginTop: "12px", display: "flex", gap: "10px", flexWrap: "wrap", alignItems: "flex-end" }}>
              <div>
                <label style={{ fontSize: "0.75rem", color: "#888", display: "block", marginBottom: "4px" }}>Select SKU</label>
                <select value={mapping.sku} onChange={e => setMapping(m => ({ ...m, sku: e.target.value }))}
                  style={{ padding: "7px 10px", border: "1px solid #e0e0e0", borderRadius: "6px", fontSize: "0.875rem" }}>
                  <option value="">-- Select unmapped SKU --</option>
                  {unmappedSkus.map(s => <option key={s.sku} value={s.sku}>{s.sku}</option>)}
                </select>
              </div>
              <div>
                <label style={{ fontSize: "0.75rem", color: "#888", display: "block", marginBottom: "4px" }}>Fulfillment</label>
                <select value={mapping.fulfillment_type} onChange={e => setMapping(m => ({ ...m, fulfillment_type: e.target.value }))}
                  style={{ padding: "7px 10px", border: "1px solid #e0e0e0", borderRadius: "6px", fontSize: "0.875rem" }}>
                  <option value="FBA">FBA</option>
                  <option value="FBM">FBM</option>
                </select>
              </div>
              <button className="btn btn-primary btn-sm"
                onClick={() => mapSku.mutate({ productId: product.id, sku: mapping.sku, fulfillment_type: mapping.fulfillment_type })}>
                Map
              </button>
            </div>
          )}

          {/* SKUs table */}
          {product.skus.length > 0 && (
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.875rem", marginTop: "12px" }}>
              <thead>
                <tr>
                  <th style={{ textAlign: "left", padding: "8px 12px", background: "#fafafa", fontSize: "0.75rem", color: "#888" }}>SKU</th>
                  <th style={{ textAlign: "left", padding: "8px 12px", background: "#fafafa", fontSize: "0.75rem", color: "#888" }}>Fulfillment</th>
                  <th style={{ padding: "8px 12px", background: "#fafafa" }}></th>
                </tr>
              </thead>
              <tbody>
                {product.skus.map(s => (
                  <tr key={s.sku}>
                    <td style={{ padding: "8px 12px" }}><code style={{ fontSize: "0.8rem" }}>{s.sku}</code></td>
                    <td style={{ padding: "8px 12px" }}>
                      <span className={`badge ${s.fulfillment_type === "FBA" ? "green" : "orange"}`}>{s.fulfillment_type}</span>
                    </td>
                    <td style={{ padding: "8px 12px", textAlign: "right" }}>
                      <button onClick={() => unmapSku.mutate(s.sku)}
                        style={{ background: "none", border: "none", cursor: "pointer", color: "#e53e3e" }}>
                        <Trash2 size={14} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      ))}
    </div>
  );
}
